import { Helmet } from "react-helmet-async";
import { useI18n } from "../i18n/LanguageContext";

export const SITE_URL = "https://offplandubai.nl";
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

const DEFAULT_KEYWORDS =
  "off-plan Dubai, off plan property Dubai, buy property in Dubai, Dubai real estate investment, invest in Dubai property, off-plan projects Dubai, Dubai property for foreigners, vastgoed Dubai kopen, investeren in Dubai vastgoed, off plan Dubai Nederland, distress deals Dubai, Dubai property investment partner, new launches Dubai, freehold property Dubai";

export const Seo = ({
  title,
  description,
  keywords,
  path = "/",
  image = OG_IMAGE,
  type = "website",
  jsonLd,
  noindex = false,
}) => {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title
    ? `${title} | OffplanDubai.nl`
    : "OffplanDubai.nl — Invest in Off-Plan Property in Dubai";
  const desc =
    description ||
    "Buy the highest-yielding off-plan property in Dubai with zero buyer commission. Curated new launches, exclusive distress deals and end-to-end guidance for Dutch & European investors.";

  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const { lang } = useI18n();
  const ogLocale = lang === "nl" ? "nl_NL" : "en_US";
  const altLocale = lang === "nl" ? "en_US" : "nl_NL";

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={keywords || DEFAULT_KEYWORDS} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="en" href={`${url}?lang=en`} />
      <link rel="alternate" hrefLang="nl" href={`${url}?lang=nl`} />
      <link rel="alternate" hrefLang="x-default" href={url} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-image-preview:large" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="OffplanDubai.nl" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={altLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

/* ---- Structured data helpers ---- */

export const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  name: "OffplanDubai.nl",
  url: SITE_URL,
  image: OG_IMAGE,
  logo: `${SITE_URL}/og-image.jpg`,
  description:
    "OffplanDubai.nl helps Dutch and European investors buy the highest-yielding off-plan property in Dubai with zero buyer commission.",
  email: "info@offplandubai.nl",
  telephone: "+31610452261",
  areaServed: ["Dubai", "United Arab Emirates", "Netherlands", "Europe"],
  address: { "@type": "PostalAddress", addressLocality: "Dubai", addressCountry: "AE" },
  sameAs: ["https://nl.trustpilot.com/review/offplandubai.nl"],
  aggregateRating: { "@type": "AggregateRating", ratingValue: "4.7", reviewCount: "436" },
};

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
